import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import NewButton from '../../../components/NewButton/NewButton';
import { useLanguage } from '../../../contexts/LanguageContext';

// Mock data for demonstration
const mockItems = [
    { id: '1', part_code: 'ANGC.442025.AA', part_name: 'Insulation Assy - Roof Center', brand: 'BREDA MENARINI BUS', unit: 'AD', price: 728, currency: 'EUR', description: 'EXPORT YP PRICE LIST EUR', part_also: 'INSULATION ASSY - ROOF CENTER' },
    { id: '2', part_code: 'ANGC.442026.AA', part_name: 'Insulation Assy - Roof Front', brand: 'BREDA MENARINI BUS', unit: 'AD', price: 695, currency: 'EUR', description: 'EXPORT YP PRICE LIST EUR', part_also: 'INSULATION ASSY - ROOF FRONT' },
    { id: '3', part_code: 'ANGC.442027.AA', part_name: 'Roof Panel - Left Side', brand: 'BREDA MENARINI BUS', unit: 'AD', price: 532, currency: 'EUR', description: 'EXPORT YP PRICE LIST EUR', part_also: 'ROOF PANEL - LEFT SIDE' },
    { id: '4', part_code: 'ANGC.442028.AA', part_name: 'Roof Panel - Right Side', brand: 'BREDA MENARINI BUS', unit: 'AD', price: 532, currency: 'EUR', description: 'EXPORT YP PRICE LIST EUR', part_also: 'ROOF PANEL - RIGHT SIDE' },
    { id: '5', part_code: 'ANGC.442029.AA', part_name: 'Insulation Assy - Side Wall', brand: 'BREDA MENARINI BUS', unit: 'AD', price: 685, currency: 'EUR', description: 'EXPORT YP PRICE LIST EUR', part_also: 'INSULATION ASSY - SIDE WALL' }
];

const currencies = [
    { name: 'EUR', code: 'EUR' },
    { name: 'USD', code: 'USD' },
    { name: 'TRY', code: 'TRY' },
    { name: 'GBP', code: 'GBP' }
];

const units = [
    { name: 'AD', code: 'AD' },
    { name: 'KG', code: 'KG' },
    { name: 'LT', code: 'LT' },
    { name: 'MT', code: 'MT' }
];

const brands = [
    { name: 'BREDA MENARINI BUS', code: 'BREDA MENARINI BUS' },
    { name: 'KARSAN', code: 'KARSAN' },
    { name: 'ISUZU', code: 'ISUZU' },
    { name: 'MERCEDES', code: 'MERCEDES' }
];

const SystemItemsPage = () => {
    const { th } = useLanguage();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        part_code: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        },
        part_name: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        brand: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    const [displayModal, setDisplayModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState({
        part_code: '',
        part_name: '',
        brand: '',
        unit: '',
        price: 0,
        currency: '',
        description: '',
        part_also: ''
    });

    useEffect(() => {
        setItems(mockItems);
        setLoading(false);
    }, []);

    useEffect(() => {
        if (selectedItem) {
            setFormData({
                part_code: selectedItem.part_code,
                part_name: selectedItem.part_name,
                brand: selectedItem.brand,
                unit: selectedItem.unit,
                price: selectedItem.price,
                currency: selectedItem.currency,
                description: selectedItem.description,
                part_also: selectedItem.part_also
            });
        } else {
            setFormData({
                part_code: '',
                part_name: '',
                brand: '',
                unit: '',
                price: 0,
                currency: '',
                description: '',
                part_also: ''
            });
        }
    }, [selectedItem]);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setDisplayModal(true);
    };

    const handleSave = () => {
        // Validation
        if (!formData.part_code || !formData.part_name || !formData.brand) {
            return; // Basic validation
        }

        if (selectedItem) {
            // Update existing item
            const updatedItems = items.map((item) =>
                item.id === selectedItem.id
                    ? {
                          ...item,
                          part_code: formData.part_code,
                          part_name: formData.part_name,
                          brand: formData.brand,
                          unit: formData.unit,
                          price: formData.price,
                          currency: formData.currency,
                          description: formData.description,
                          part_also: formData.part_also
                      }
                    : item
            );
            setItems(updatedItems);
        } else {
            // Add new item
            const newItem = {
                id: Math.floor(Math.random() * 10000).toString(),
                part_code: formData.part_code,
                part_name: formData.part_name,
                brand: formData.brand,
                unit: formData.unit,
                price: formData.price,
                currency: formData.currency,
                description: formData.description,
                part_also: formData.part_also
            };
            setItems([...items, newItem]);
        }

        setDisplayModal(false);
    };

    const handleRemove = () => {
        if (selectedItem) {
            const filteredItems = items.filter((item) => item.id !== selectedItem.id);
            setItems(filteredItems);
            setDisplayModal(false);
        }
    };

    const handleExport = () => {
        console.log('Excel Export');
        // Excel export functionality would be implemented here
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <i onClick={() => handleEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')}></i>
                <i className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')}></i>
            </div>
        );
    };

    const priceBodyTemplate = (rowData) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: rowData.currency }).format(rowData.price);
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center">
                {/* <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder={th('systemItems.searchPlaceholder')} />
                </span> */}
            </div>
            <div className="flex gap-2">
                <Button label={th('systemItems.excelExport')} icon="pi pi-file-excel" severity="success" onClick={handleExport} />
            </div>
        </div>
    );

    const footerContent = (
        <div className="flex justify-content-end gap-2">
            {selectedItem && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" onClick={handleRemove} />}
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={() => setDisplayModal(false)} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSave} />
        </div>
    );

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('systemItems.title')}</h1>
            </div>

            <Card>
                <DataTable
                    value={items}
                    paginator
                    className="p-datatable-gridlines"
                    showGridlines
                    rows={10}
                    dataKey="id"
                    loading={loading}
                    filters={filters}
                    filterDisplay="menu"
                    globalFilterFields={['part_code', 'part_name', 'brand', 'description', 'part_also']}
                    header={header}
                    emptyMessage={th('systemItems.emptyMessage')}
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                    tableStyle={{ minWidth: '50rem' }}
                >
                    <Column sortable bodyClassName="text-center" headerClassName="text-center" body={actionBodyTemplate} exportable={false} style={{ width: '5%', minWidth: '33px' }}></Column>
                    <Column field="part_code" header={th('systemItems.partCode')} filter filterPlaceholder={th('systemItems.searchPartCodePlaceholder')} sortable style={{ width: '15%' }}></Column>
                    <Column field="part_name" header={th('systemItems.partName')} filter filterPlaceholder={th('systemItems.searchPartNamePlaceholder')} sortable style={{ width: '15%' }}></Column>
                    <Column field="brand" header={th('systemItems.brand')} filter filterPlaceholder={th('systemItems.searchBrandPlaceholder')} sortable style={{ width: '15%' }}></Column>
                    <Column field="unit" header={th('systemItems.unit')} sortable style={{ width: '8%' }}></Column>
                    <Column field="price" header={th('systemItems.price')} body={priceBodyTemplate} sortable style={{ width: '10%' }}></Column>
                    <Column field="description" header={th('systemItems.description')} sortable style={{ width: '15%' }}></Column>
                    <Column field="part_also" header={th('systemItems.partName2')} sortable style={{ width: '15%' }}></Column>
                </DataTable>
            </Card>

            <Dialog header={selectedItem ? th('systemItems.editTitle') : th('systemItems.newTitle')} visible={displayModal} style={{ width: '650px' }} modal footer={footerContent} onHide={() => setDisplayModal(false)}>
                <div className="flex flex-column gap-4 p-3">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="part_code" className="font-bold block mb-2">
                                    {th('systemItems.partCode')}*
                                </label>
                                <InputText id="part_code" value={formData.part_code} onChange={(e) => setFormData({ ...formData, part_code: e.target.value })} className="w-full" required />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="part_name" className="font-bold block mb-2">
                                    {th('systemItems.partName')}*
                                </label>
                                <InputText id="part_name" value={formData.part_name} onChange={(e) => setFormData({ ...formData, part_name: e.target.value })} className="w-full" required />
                            </div>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="brand" className="font-bold block mb-2">
                                    {th('systemItems.brand')}*
                                </label>
                                <Dropdown id="brand" value={formData.brand} options={brands} onChange={(e) => setFormData({ ...formData, brand: e.value })} optionLabel="name" optionValue="code" className="w-full" required />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="unit" className="font-bold block mb-2">
                                    {th('systemItems.unit')}
                                </label>
                                <Dropdown id="unit" value={formData.unit} options={units} onChange={(e) => setFormData({ ...formData, unit: e.value })} optionLabel="name" optionValue="code" className="w-full" />
                            </div>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="price" className="font-bold block mb-2">
                                    {th('systemItems.price')}
                                </label>
                                <InputNumber id="price" value={formData.price} onValueChange={(e) => setFormData({ ...formData, price: e.value })} mode="decimal" minFractionDigits={2} className="w-full" />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="currency" className="font-bold block mb-2">
                                    {th('systemItems.currency')}
                                </label>
                                <Dropdown id="currency" value={formData.currency} options={currencies} onChange={(e) => setFormData({ ...formData, currency: e.value })} optionLabel="name" optionValue="code" className="w-full" />
                            </div>
                        </div>
                    </div>

                    <div className="field">
                        <label htmlFor="description" className="font-bold block mb-2">
                            {th('systemItems.description')}
                        </label>
                        <InputText id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full" />
                    </div>

                    <div className="field">
                        <label htmlFor="part_also" className="font-bold block mb-2">
                            {th('systemItems.partName2')}
                        </label>
                        <InputText id="part_also" value={formData.part_also} onChange={(e) => setFormData({ ...formData, part_also: e.target.value })} className="w-full" />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default SystemItemsPage;
